import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Anchor, Ticket, Mail, Home, Compass } from "lucide-react";
import { usePageSEO } from "@/hooks/use-page-seo";

const NotFound = () => {
  const location = useLocation();

  usePageSEO({
    title: "Lost at Sea — Yacht Rock Detroit",
    description: "This page doesn't exist. Navigate back to smoother waters.",
  });

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="relative min-h-screen bg-yacht-blue overflow-hidden flex items-center justify-center px-6">
      {/* Animated wave layers */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-40 opacity-10"
          style={{
            background: "repeating-linear-gradient(0deg, transparent, transparent 20px, hsl(var(--yacht-cream)) 20px, hsl(var(--yacht-cream)) 21px)",
          }}
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-64 opacity-5"
          style={{
            background: "radial-gradient(ellipse at 50% 100%, hsl(var(--yacht-gold)) 0%, transparent 70%)",
          }}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Floating anchor icon */}
      <motion.div
        className="absolute top-[15%] right-[15%] text-yacht-cream/5"
        animate={{ y: [0, -20, 0], rotate: [0, 5, -5, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <Anchor size={180} strokeWidth={1} />
      </motion.div>

      <motion.div
        className="absolute bottom-[20%] left-[10%] text-yacht-cream/5"
        animate={{ y: [0, 15, 0], rotate: [0, -3, 3, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      >
        <Compass size={120} strokeWidth={1} />
      </motion.div>

      {/* Main content */}
      <div className="relative z-10 text-center max-w-lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* 404 number */}
          <motion.h1
            className="font-serif text-[8rem] md:text-[12rem] leading-none text-yacht-cream/10 font-bold select-none"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            404
          </motion.h1>

          {/* Headline */}
          <h2 className="font-serif text-3xl md:text-5xl text-yacht-cream -mt-10 md:-mt-16 relative z-10">
            You've sailed too far.
          </h2>

          <p className="text-yacht-cream/50 text-sm md:text-base mt-4 max-w-sm mx-auto">
            Looks like this page drifted out to sea. Let's get you back to smoother waters.
          </p>
        </motion.div>

        {/* Quick links */}
        <motion.div
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Link
            to="/"
            className="group flex items-center gap-2.5 bg-yacht-yellow text-yacht-blue font-semibold text-sm rounded-full px-6 py-3 hover:bg-yacht-cream transition-colors"
          >
            <Home size={16} />
            Back to Shore
          </Link>
          <Link
            to="/tickets"
            className="group flex items-center gap-2.5 bg-yacht-cream/10 backdrop-blur-sm border border-yacht-cream/20 text-yacht-cream font-medium text-sm rounded-full px-6 py-3 hover:bg-yacht-cream/20 transition-colors"
          >
            <Ticket size={16} />
            Get Tickets
          </Link>
        </motion.div>

        {/* Secondary links */}
        <motion.div
          className="mt-6 flex items-center justify-center gap-6 text-yacht-cream/40 text-xs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Link to="/lineup" className="hover:text-yacht-cream transition-colors">
            Lineup
          </Link>
          <span className="w-1 h-1 rounded-full bg-yacht-cream/20" />
          <Link to="/guide" className="hover:text-yacht-cream transition-colors">
            Festival Guide
          </Link>
          <span className="w-1 h-1 rounded-full bg-yacht-cream/20" />
          <Link to="/contact" className="hover:text-yacht-cream transition-colors">
            Contact
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;
